import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { Alert, Button, Flex, Form, Input, message, Popover, Typography } from 'antd';
import { CircleHelp } from 'lucide-react';
import { useEffect } from 'react';

import {
  getListOrganizationAiMemoriesQueryKey,
  type ListAiMemoriesResponseDtoItemsItem,
  useListOrganizationAiMemories,
  useUpdateOrganizationAiMemory,
} from '../../../../api';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { Loader } from '../../../../ui/components/loader';

const CATEGORY_LABEL: Record<string, string> = {
  user_preference: 'User preferences',
  workflow_pattern: 'Workflow patterns',
  communication_style: 'Communication style',
  correction: 'Corrections',
  entity_insight: 'Entity insights',
};

const CATEGORY_INFO: Record<string, string> = {
  user_preference:
    'Things you have told the assistant you want it to remember, or that it picked up from how you work—used to match your stated style and priorities.',
  workflow_pattern:
    'Habits and sequences the assistant noticed in how you get work done, so suggestions can follow your usual approach instead of a generic flow.',
  communication_style:
    'How you prefer to sound in writing (length, tone, formality), often learned from edits you make to drafts and messages sent in this workspace.',
  correction:
    'Feedback when you change or reject the assistant’s output. It uses this to avoid repeating the same mistakes.',
  entity_insight:
    'Notes about specific people, companies, or records you have discussed, so replies can stay consistent with context you have shared.',
};

const SettingsPersonalAiPreferencesRoute = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const organizationId = selectedOrganization.id;

  const [form] = Form.useForm();
  const { data, status, error, refetch } = useListOrganizationAiMemories(organizationId);

  const items = data?.data.items ?? [];

  const grouped = new Map<string, ListAiMemoriesResponseDtoItemsItem[]>();
  for (const item of items) {
    const cur = grouped.get(item.category) ?? [];
    cur.push(item);
    grouped.set(item.category, cur);
  }

  const sortedCategories = Array.from(grouped.entries())
    .filter(([, list]) => list.length > 0)
    .map(([cat]) => cat)
    .sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    const list = data?.data.items ?? [];
    form.setFieldsValue(Object.fromEntries(list.map((i) => [i.id, i.content])));
  }, [form, data]);

  const { mutate: updateMemory, isPending: updatePending } = useUpdateOrganizationAiMemory({
    mutation: {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: getListOrganizationAiMemoriesQueryKey(organizationId),
        });
        message.success(variables.data.content.trim() === '' ? 'Removed' : 'Saved');
      },
      onError: () => message.error('Failed to save'),
    },
  });

  return (
    <SettingsLayoutContent
      title="Personal AI preferences"
      description="Your AI memories and preferences for this workspace—personal to you, not shared across the organization."
    >
      {status === 'pending' ? (
        <Loader />
      ) : status === 'error' ? (
        <Flex vertical gap={8}>
          <Typography.Paragraph type="danger">
            {error instanceof Error ? error.message : 'Failed to load preferences'}
          </Typography.Paragraph>
          <Button size="small" onClick={() => refetch()} htmlType="button">
            Try again
          </Button>
        </Flex>
      ) : (
        <Flex vertical gap={16}>
          <Alert
            showIcon
            type="info"
            title="Personal to you"
            description="Learned from your assistant chats, message edits, and activity. Used when the AI works on your behalf. Manage entries below by category."
          />
          {items.length === 0 ? (
            <Typography.Paragraph type="secondary">No memories yet.</Typography.Paragraph>
          ) : (
            <Form form={form} layout="vertical" disabled={updatePending}>
              {sortedCategories.map((category) => (
                <Flex key={category} vertical gap={8}>
                  {(grouped.get(category) ?? []).map((item) => (
                    <Form.Item
                      key={`${item.id}-${item.updatedAt}`}
                      name={item.id}
                      label={
                        <Flex align="center">
                          {CATEGORY_LABEL[category]}
                          {CATEGORY_INFO[category] ? (
                            <Popover
                              trigger={['hover', 'click']}
                              content={CATEGORY_INFO[category]}
                              styles={{ content: { maxWidth: 360 } }}
                            >
                              <Button
                                type="link"
                                size="small"
                                icon={<CircleHelp size={14} />}
                                style={{ color: 'var(--ant-color-text-secondary)' }}
                              />
                            </Popover>
                          ) : null}
                        </Flex>
                      }
                    >
                      <Input.TextArea
                        rows={3}
                        autoSize={{ minRows: 4, maxRows: 8 }}
                        onBlur={(e) =>
                          updateMemory({
                            organizationId,
                            memoryId: item.id,
                            data: { content: e.currentTarget.value },
                          })
                        }
                        placeholder="Clear this field and leave focus to remove this entry."
                      />
                    </Form.Item>
                  ))}
                </Flex>
              ))}
            </Form>
          )}
        </Flex>
      )}
    </SettingsLayoutContent>
  );
};

export const Route = createFileRoute('/_authenticated/_app/settings/personal-ai-preferences')({
  component: SettingsPersonalAiPreferencesRoute,
});
